import React, { useEffect, useState } from 'react';
import RialSymbol from '../components/RialSymbol';
import { useNavigate } from 'react-router-dom';
import customerService, { Cart as CartType } from '../services/customer.service';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  IconButton,
  TextField,
  Divider,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  InputAdornment,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
  ShoppingCart as CartIcon,
  LocalOffer as CouponIcon,
} from '@mui/icons-material';
import LoadingAnimation from '../components/LoadingAnimation';

export default function Cart() {
  const navigate = useNavigate();
  const [cart, setCart] = useState<CartType | null>(null);
  const [loading, setLoading] = useState(true);
  const [couponCode, setCouponCode] = useState('');
  const [couponApplied, setCouponApplied] = useState(false);
  const [discount, setDiscount] = useState(0);
  const [message, setMessage] = useState<{ text: string; type: 'success' | 'error' | 'info' | 'warning' } | null>(null);

  useEffect(() => {
    loadCart();
  }, []);

  const loadCart = async () => {
    try {
      const cartData = await customerService.getCart();
      setCart(cartData);
    } catch (error) {
      console.error('Error loading cart:', error);
      setMessage({ text: 'Failed to load cart', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateQuantity = async (itemId: string, newQuantity: number, item: any) => {
    if (newQuantity < 1) return;

    // Check inventory availability
    const availableStock = item.surpriseBox?.inventory?.remainingBoxes || 0;

    if (newQuantity > availableStock) {
      setMessage({
        text: `Only ${availableStock} available in stock`,
        type: 'warning'
      });
      return;
    }

    // Store original cart for rollback
    const originalCart = cart;

    // Optimistic update - update UI immediately
    if (cart) {
      const updatedItems = cart.items.map((cartItem: any) =>
        cartItem._id === itemId ? { ...cartItem, quantity: newQuantity } : cartItem
      );
      const newTotalItems = updatedItems.reduce((sum: number, i: any) => sum + i.quantity, 0);
      const newTotalAmount = updatedItems.reduce((sum: number, i: any) => sum + (i.price * i.quantity), 0);

      setCart({
        ...cart,
        items: updatedItems,
        totalItems: newTotalItems,
        totalAmount: newTotalAmount
      });
      setMessage({ text: 'Quantity updated', type: 'success' });
    }

    // Sync with backend
    try {
      const updatedCart = await customerService.updateCartItem(itemId, newQuantity);
      setCart(updatedCart);
    } catch (error: any) {
      console.error('Error updating quantity:', error);
      // Revert on error
      setCart(originalCart);
      setMessage({ text: 'Failed to update quantity', type: 'error' });
    }
  };

  const handleRemoveItem = async (itemId: string) => {
    // Store original cart for rollback
    const originalCart = cart;

    // Optimistic update - remove immediately from UI
    if (cart) {
      const updatedItems = cart.items.filter((item: any) => item._id !== itemId);
      const newTotalItems = updatedItems.reduce((sum: number, i: any) => sum + i.quantity, 0);
      const newTotalAmount = updatedItems.reduce((sum: number, i: any) => sum + (i.price * i.quantity), 0);

      setCart({
        ...cart,
        items: updatedItems,
        totalItems: newTotalItems,
        totalAmount: newTotalAmount
      });
      setMessage({ text: 'Item removed from cart', type: 'success' });
    }

    // Sync with backend
    try {
      const updatedCart = await customerService.removeFromCart(itemId);
      setCart(updatedCart);
    } catch (error: any) {
      console.error('Error removing item:', error);
      // Revert on error
      setCart(originalCart);
      setMessage({ text: 'Failed to remove item', type: 'error' });
    }
  };

  const handleClearCart = async () => {
    if (!window.confirm('Are you sure you want to clear your cart?')) return;

    try {
      const updatedCart = await customerService.clearCart();
      setCart(updatedCart);
      setMessage({ text: 'Cart cleared', type: 'info' });
    } catch (error: any) {
      console.error('Error clearing cart:', error);
      setMessage({ text: 'Failed to clear cart', type: 'error' });
    }
  };

  const handleApplyCoupon = () => {
    // Mock coupon validation
    const validCoupons: { [key: string]: number } = {
      'SAVE10': 10,
      'SAVE20': 20,
      'BITEHUB': 15,
      'WELCOME': 5,
    };

    const couponUpper = couponCode.toUpperCase();
    if (validCoupons[couponUpper]) {
      setDiscount(validCoupons[couponUpper]);
      setCouponApplied(true);
      setMessage({ text: `Coupon applied! ${validCoupons[couponUpper]}% discount`, type: 'success' });
    } else {
      setMessage({ text: 'Invalid coupon code', type: 'error' });
    }
  };

  const handleRemoveCoupon = () => {
    setCouponCode('');
    setCouponApplied(false);
    setDiscount(0);
    setMessage({ text: 'Coupon removed', type: 'info' });
  };

  const calculateTotal = () => {
    if (!cart) return 0;
    const subtotal = cart.totalAmount;
    const discountAmount = (subtotal * discount) / 100;
    return subtotal - discountAmount;
  };

  const handleBuyNow = () => {
    if (!cart || cart.items.length === 0) {
      setMessage({ text: 'Your cart is empty', type: 'error' });
      return;
    }

    navigate('/checkout', {
      state: {
        cart,
        total: calculateTotal(),
        discount,
        couponCode: couponApplied ? couponCode : '',
      },
    });
  };

  if (loading) {
    return <LoadingAnimation message="Loading your cart..." subtitle="Preparing your order" />;
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>
            Shopping Cart
          </Typography>
          <Typography color="text.secondary">
            {cart?.totalItems || 0} item{cart?.totalItems !== 1 ? 's' : ''} in your cart
          </Typography>
        </Box>
        {cart && cart.items.length > 0 && (
          <Button variant="outlined" color="error" onClick={handleClearCart}>
            Clear Cart
          </Button>
        )}
      </Box>

      {/* Message Alert */}
      {message && (
        <Alert severity={message.type} sx={{ mb: 3 }} onClose={() => setMessage(null)}>
          {message.text}
        </Alert>
      )}

      {/* Empty Cart */}
      {!cart || cart.items.length === 0 ? (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 8 }}>
            <CartIcon sx={{ fontSize: 80, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" sx={{ mb: 2 }}>
              Your cart is empty
            </Typography>
            <Typography color="text.secondary" sx={{ mb: 3 }}>
              Start adding surprise boxes to your cart!
            </Typography>
            <Button variant="contained" onClick={() => navigate('/surprise-boxes')}>
              Browse Surprise Boxes
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '2fr 1fr' }, gap: 3 }}>
          {/* Cart Items */}
          <Card>
            <CardContent>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Item</TableCell>
                      <TableCell align="center">Price</TableCell>
                      <TableCell align="center">Quantity</TableCell>
                      <TableCell align="right">Total</TableCell>
                      <TableCell align="right">Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {cart.items.map((item) => (
                      <TableRow key={item._id}>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Box
                              component="img"
                              src={item.surpriseBox?.images?.[0] || 'https://via.placeholder.com/80'}
                              alt={item.surpriseBox?.name}
                              sx={{
                                width: 80,
                                height: 80,
                                objectFit: 'cover',
                                borderRadius: 1,
                                border: '1px solid #e0e0e0',
                              }}
                            />
                            <Box>
                              <Typography sx={{ fontWeight: 600 }}>
                                {item.surpriseBox?.name || 'Surprise Box'}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {item.surpriseBox?.business?.name || 'Merchant'}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell align="center">
                          <Typography sx={{ fontWeight: 600 }}><RialSymbol /> {item.price.toFixed(2)}</Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                            <IconButton
                              size="small"
                              onClick={() => handleUpdateQuantity(item._id, item.quantity - 1, item)}
                              disabled={item.quantity <= 1}
                            >
                              <RemoveIcon fontSize="small" />
                            </IconButton>
                            <Typography sx={{ minWidth: 30, textAlign: 'center', fontWeight: 600 }}>
                              {item.quantity}
                            </Typography>
                            <IconButton
                              size="small"
                              onClick={() => handleUpdateQuantity(item._id, item.quantity + 1, item)}
                              disabled={item.quantity >= (item.surpriseBox?.inventory?.remainingBoxes || 0)}
                              title={
                                item.quantity >= (item.surpriseBox?.inventory?.remainingBoxes || 0)
                                  ? 'Maximum stock reached'
                                  : 'Add one more'
                              }
                            >
                              <AddIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </TableCell>
                        <TableCell align="right">
                          <Typography sx={{ fontWeight: 700 }}>
                            <RialSymbol /> {(item.price * item.quantity).toFixed(2)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <IconButton color="error" onClick={() => handleRemoveItem(item._id)}>
                            <DeleteIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>

          {/* Order Summary */}
          <Box>
            {/* Coupon Section */}
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                  Apply Coupon
                </Typography>
                {!couponApplied ? (
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="Enter coupon code"
                      value={couponCode}
                      onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                      slotProps={{
                        input: {
                          startAdornment: (
                            <InputAdornment position="start">
                              <CouponIcon fontSize="small" />
                            </InputAdornment>
                          ),
                        },
                      }}
                    />
                    <Button variant="contained" onClick={handleApplyCoupon} disabled={!couponCode}>
                      Apply
                    </Button>
                  </Box>
                ) : (
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography sx={{ fontWeight: 600, color: 'success.main' }}>
                        {couponCode} Applied
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {discount}% discount
                      </Typography>
                    </Box>
                    <Button size="small" variant="outlined" onClick={handleRemoveCoupon}>
                      Remove
                    </Button>
                  </Box>
                )}
                <Divider sx={{ my: 2 }} />
                <Typography variant="caption" color="text.secondary">
                  Available coupons: SAVE10, SAVE20, BITEHUB, WELCOME
                </Typography>
              </CardContent>
            </Card>

            {/* Summary */}
            <Card>
              <CardContent>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
                  Order Summary
                </Typography>

                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                  <Typography color="text.secondary">Subtotal ({cart.totalItems} items)</Typography>
                  <Typography sx={{ fontWeight: 600 }}><RialSymbol /> {cart.totalAmount.toFixed(2)}</Typography>
                </Box>

                {discount > 0 && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                    <Typography color="success.main">Discount ({discount}%)</Typography>
                    <Typography sx={{ fontWeight: 600, color: 'success.main' }}>
                      - <RialSymbol /> {((cart.totalAmount * discount) / 100).toFixed(2)}
                    </Typography>
                  </Box>
                )}

                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                  <Typography color="text.secondary">Delivery Fee</Typography>
                  <Typography sx={{ fontWeight: 600, color: 'success.main' }}>FREE</Typography>
                </Box>

                <Divider sx={{ my: 2 }} />

                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    Total
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: 'primary.main' }}>
                    <RialSymbol /> {calculateTotal().toFixed(2)}
                  </Typography>
                </Box>

                <Button
                  fullWidth
                  variant="contained"
                  size="large"
                  onClick={handleBuyNow}
                  sx={{ mb: 1 }}
                >
                  Buy Now
                </Button>

                <Button
                  fullWidth
                  variant="outlined"
                  onClick={() => navigate('/surprise-boxes')}
                >
                  Continue Shopping
                </Button>
              </CardContent>
            </Card>

            {/* Savings Info */}
            <Card sx={{ mt: 3, background: 'linear-gradient(135deg, #0B6E4F 0%, #B1D65D 100%)', color: 'white' }}>
              <CardContent>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  🌍 Environmental Impact
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  By purchasing {cart.totalItems} box{cart.totalItems !== 1 ? 'es' : ''}, you're helping reduce food waste!
                </Typography>
              </CardContent>
            </Card>
          </Box>
        </Box>
      )}
    </Box>
  );
}
